import { Component, OnInit, computed, signal, inject, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import { TranslocoPipe } from "@jsverse/transloco";
import { LorenErrorHandlingService } from "../../../../shared/services/loren-error-handling-service/loren-error-handling-service.service";
import { LorenImageModule } from "../../../../shared/pipes/loren-image/loren-image.module";
import { LorenAssetTypeService } from "../../../loren-assets/services/loren-asset-type-service/loren-asset-type.service";
import { LorenGroupService } from "../../../loren-groups/services/loren-group-service/loren-group.service";
import { LorenAssetUtilsService } from "../../../loren-assets/services/loren-asset-utils-service/loren-asset-utils.service";
import { LorenAssetPropertiesService } from "../../../loren-assets/services/loren-asset-properties-service/loren-asset-properties.service";
import { LorenAssetDetailsBaseInformationComponent } from "../loren-asset-details-base-information/loren-asset-details-base-information/loren-asset-details-base-information.component";
import { LorenPageContentComponent } from "../../../../shared/modules/loren-page-content/loren-page-content.component";
import { LorenAssetDetailsMap } from "../loren-asset-details-map/loren-asset-details-map.component";
import { LorenAssetDetailsInfoTableComponent } from "../loren-asset-details-info-table/loren-asset-details-info-table.component";
import { LorenAssetDetailsChartsComponent } from "../loren-asset-details-charts/loren-asset-details-charts.component";
import { LorenAssetDetailsAnalysisComponent } from "../loren-asset-details-analysis/loren-asset-details-analysis.component";
import { LorenAssetDetailsPhotosComponent } from "../loren-asset-details-photos/loren-asset-details-photos.component";
import { LorenAssetDetailsAlarmsTableComponent } from "../loren-asset-details-alarms-table/loren-asset-details-alarms-table.component";
import { LorenMeteringType } from "../../../loren-assets/interfaces/loren-metering-type";
import { LorenFeatureToggleService } from "../../../../shared/services/loren-feature-toggle-service/loren-feature-toggle.service";
import { LorenFeature } from "../../../../shared/enums/loren-feature.enum";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import { AssetService } from "../../../asset/asset.service";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { switchMap, combineLatest, forkJoin, Observable } from "rxjs";
import { distinctUntilChanged, map, shareReplay, startWith } from "rxjs/operators";
import { isDeepEqual } from "remeda";
import { LorenAssetType } from "../../../loren-assets/interfaces/loren-asset-type.interface";

@Component({
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatTabGroup,
    MatTab,
    TranslocoPipe,
    LorenImageModule,
    LorenPageContentComponent,
    LorenAssetDetailsBaseInformationComponent,
    LorenAssetDetailsMap,
    LorenAssetDetailsInfoTableComponent,
    LorenAssetDetailsChartsComponent,
    LorenAssetDetailsAnalysisComponent,
    LorenAssetDetailsPhotosComponent,
    LorenAssetDetailsAlarmsTableComponent,
  ],
  selector: "loren-loren-asset",
  standalone: true,
  styleUrl: "./loren-asset-details.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./loren-asset-details.component.html",
})
export class LorenAssetDetailsComponent
  extends LorenBaseFeature
  implements OnInit
{
  private router = inject(Router);
  errorHandlingService = inject(LorenErrorHandlingService);
  private assetTypeService = inject(LorenAssetTypeService);
  private groupService = inject(LorenGroupService);
  private propertiesService = inject(LorenAssetPropertiesService);
  private featureToggleService = inject(LorenFeatureToggleService);

  private readonly assets = inject(AssetService);
  private readonly activatedRoute = inject(ActivatedRoute);

  readonly tabLabels = [
    { label: "asset_details.details", name: "details" },
    { label: "asset_details.diagrams", name: "diagrams" },
    { label: "asset_details.alarms", name: "alarms" },
    { label: "asset_details.photos", name: "photos" },
  ];

  asset$ = this.activatedRoute.params.pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap(params => this.assets.get(+params.id)),
  )

  activeTab$ = this.activatedRoute.fragment.pipe(
    takeUntilDestroyed(this.destroyRef),
    map(fragment => this.tabLabels.findIndex((l) => l.name === fragment))
  )

  groups$ = this.asset$.pipe(
    takeUntilDestroyed(this.destroyRef),
    map(asset => asset.belongsToGroups),
    distinctUntilChanged((previous, current) => isDeepEqual(previous, current)),
    switchMap(groups => forkJoin(groups.map(g => this.groupService.get(g)))),
    map(groups => groups.map(x => x.name)),
    startWith([]),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  showPhotos$ = this.featureToggleService
    .userHasFeature(LorenFeature.IMAGE_GALLERY)
    .pipe(takeUntilDestroyed(this.destroyRef));

  hasAnalysis$ = this.asset$.pipe(
    map(asset => asset.metering.some(
      (m) =>
        m.type === LorenMeteringType.TEMPERATURE_SENSOR ||
        m.type === LorenMeteringType.HUMIDITY_SENSOR ||
        m.type === LorenMeteringType.CO2_SENSOR
    )),
  )

  asset = toSignal(this.asset$);
  properties = signal([]);

  private allAssetTypes = signal<LorenAssetType[]>([]);

  location = computed<string>(() =>
    this.asset()?.location
      ? `${this.asset().location.latitude}, ${this.asset().location.longitude}`
      : ""
  );

  assetType = computed(() => this.allAssetTypes().find((x) => x.id === this.asset().typeId));

  ngOnInit() {
    combineLatest([
      this.asset$, // Unfortunately, side effect
      this.assetTypeService.getAll() as unknown as Observable<{ items: LorenAssetType[] }>,
      this.propertiesService.query(),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([asset, assetTypes, properties]) => {
      this.allAssetTypes.set(assetTypes.items);

      const propertiesIds = LorenAssetUtilsService.getAssetTypeProperties(
        assetTypes.items,
        asset
      )

      LorenAssetUtilsService.populateAssetProperties(
        properties.items,
        propertiesIds,
        asset
      );

      this.properties.set(asset.propertiesData.map(pd => {
        const { name } = asset.properties.find(
          (p) => p.id === pd.propertyTypeId
        );

        return { name, value: pd.value };
      }));
    })
  }

  tabChangeHandler(event) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      fragment: this.tabLabels[event.index].name,
    });
  }
}
